import { useState, useEffect } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ImageData {
  src: string;
  alt: string;
}

interface ImageLightboxProps {
  images: ImageData[];
  currentIndex: number;
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

export function ImageLightbox({ images, currentIndex, isOpen, onClose, onNavigate }: ImageLightboxProps) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "ArrowLeft") {
        onNavigate(currentIndex > 0 ? currentIndex - 1 : images.length - 1);
      } else if (e.key === "ArrowRight") {
        onNavigate(currentIndex < images.length - 1 ? currentIndex + 1 : 0);
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose, onNavigate, currentIndex, images.length]);

  if (!isOpen || !images[currentIndex]) return null;

  const currentImage = images[currentIndex];
  const hasPrevious = images.length > 1;
  const hasNext = images.length > 1;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div className="relative max-h-[90vh] max-w-[90vw] flex items-center">
        {/* Previous button */}
        {hasPrevious && (
          <Button
            variant="secondary"
            size="icon"
            className="absolute -left-16 h-10 w-10 bg-white/10 hover:bg-white/20 text-white border-white/20 z-10"
            onClick={(e) => {
              e.stopPropagation();
              onNavigate(currentIndex > 0 ? currentIndex - 1 : images.length - 1);
            }}
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
        )}

        {/* Next button */}
        {hasNext && (
          <Button
            variant="secondary"
            size="icon"
            className="absolute -right-16 h-10 w-10 bg-white/10 hover:bg-white/20 text-white border-white/20 z-10"
            onClick={(e) => {
              e.stopPropagation();
              onNavigate(currentIndex < images.length - 1 ? currentIndex + 1 : 0);
            }}
          >
            <ChevronRight className="h-5 w-5" />
          </Button>
        )}

        {/* Close button */}
        <Button
          variant="secondary"
          size="icon"
          className="absolute -top-12 right-0 h-8 w-8 bg-white/10 hover:bg-white/20 text-white border-white/20 z-10"
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </Button>

        {/* Image counter */}
        {images.length > 1 && (
          <div className="absolute -top-12 left-0 px-3 py-1 bg-white/10 rounded text-white text-sm">
            {currentIndex + 1} / {images.length}
          </div>
        )}

        <img
          src={currentImage.src}
          alt={currentImage.alt}
          className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg"
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    </div>
  );
}